import { defineStore } from 'pinia';

import { authApi } from '@/api/wb';
import type { WbUser } from '@/types/api';

// P-007 安全加固：工具函数迁移到 src/utils/security.ts，便于单元测试 + 跨文件复用。
// 这里 re-export 保持向后兼容（guards.ts / 其他模块可能从 auth store 引入）。
export { getTokenRemainingMs } from '@/utils/security';

/**
 * 鉴权 Store
 *
 * === Token 存储策略（P-007 安全加固，2026-08-12 改造）===
 *
 * 主存储：Pinia state（memory ref）—— 刷新页面丢失，由 sessionStorage 恢复
 * 恢复源：sessionStorage —— 关闭 tab 自动清空，比 localStorage 更安全
 * 跨 tab 通知：localStorage 哨兵 key `wfb:auth-event` ——
 *   storage event 仅在同源不同 tab 间触发，sessionStorage 是 tab 隔离的，
 *   无法直接监听其他 tab 的登出事件，需借助 localStorage 哨兵。
 *
 * 改造前（W1 实现）：
 *   - token / user 主存 localStorage（wfb:token / wfb:user）
 *   - 与老前端 web-feedback 共享 localStorage（同域名），新老前端登录态互通
 *
 * 改造后（安全加固）：
 *   - token 主存 memory + sessionStorage（关闭 tab 自动清空，XSS 攻击窗口收窄）
 *   - 业务数据（wfb:history:v1 / wfb:analyze_task 等）仍在 localStorage（按 EXP-005
 *     数据链路约束，不迁移避免破坏历史记录恢复链路）
 *   - **Trade-off**：sessionStorage 在新 tab 不会共享，每个 tab 需独立登录
 *     （用户已确认接受，换更高安全性）
 *   - **Trade-off**：老前端 web-feedback 读 localStorage wfb:token ——
 *     Portal 写 sessionStorage 后老前端读不到 Portal 的 token，新老前端登录态不再互通。
 *     用户场景：Portal 独立 SPA 部署，老前端仅作回滚参考，此 trade-off 可接受。
 *
 * Actions 与 API 调用对应关系：
 * - login(inviteCode)：调 authApi.loginByInvite
 * - fetchMe()：调 authApi.me 刷新 user
 * - logout()：调 authApi.logout 并清空 state
 *
 * fetchMe 失败（401）时：http.ts 拦截器会调 auth.clear() 并跳 /wb/login，
 * 此处仅做错误捕获 + 控制台警告，不再二次跳转。
 */
const TOKEN_KEY = 'wfb:token';
const USER_KEY = 'wfb:user';
/**
 * 跨 tab 同步哨兵 key（localStorage）。
 *
 * sessionStorage 是 tab 隔离的，无法触发跨 tab storage event。
 * 用 localStorage 写一个时间戳哨兵：
 * - 登出时更新哨兵值（触发其他 tab 的 storage event）
 * - 登录时也更新（其他 tab 若未登录，不动作；若已登录，保持原状）
 * - 监听方判断 newValue 是否为 'logout' 哨兵，若是则跳登录页
 */
const AUTH_EVENT_KEY = 'wfb:auth-event';

function loadUser(): WbUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as WbUser) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // 初始化从 sessionStorage 恢复（刷新页面场景）。关闭 tab 时 sessionStorage 自动清空。
    token: sessionStorage.getItem(TOKEN_KEY) || '',
    user: loadUser(),
    /**
     * fetchMe 加载状态。
     * - 'idle'：未触发
     * - 'loading'：正在拉 /api/auth/me
     * - 'done'：拉取成功或失败结束
     *
     * 用于路由守卫在首次进入时阻塞渲染，避免已登录用户被误判为未登录跳登录页。
     */
    meStatus: 'idle' as 'idle' | 'loading' | 'done',
  }),
  getters: {
    isLoggedIn: (s) => !!s.token,
    isAdmin: (s) => s.user?.role === 'admin',
    trialRemaining: (s) => {
      if (!s.user) return 0;
      // trial_quota = -1 表示无限配额（wb-routes.md §3.2）
      if (s.user.trial_quota < 0) return Number.POSITIVE_INFINITY;
      return Math.max(0, s.user.trial_quota - s.user.trial_used);
    },
  },
  actions: {
    setToken(token: string) {
      this.token = token;
      // 双写 sessionStorage：刷新页面时从 sessionStorage 恢复到 memory。
      // 不写 localStorage（避免 XSS 拖库 + 关 tab 仍存活）。
      sessionStorage.setItem(TOKEN_KEY, token);
    },
    setUser(user: WbUser | null) {
      this.user = user;
      if (user) {
        sessionStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        sessionStorage.removeItem(USER_KEY);
      }
    },
    /**
     * 向其他 tab 广播鉴权事件（登录 / 登出）。
     *
     * sessionStorage 是 tab 隔离的，无法跨 tab 触发 storage event。
     * 借助 localStorage 哨兵：写入新值时同源其他 tab 会触发 storage event。
     * 用 type + timestamp 编码避免重复值被浏览器优化忽略（同值不触发 event）。
     */
    broadcastAuthEvent(type: 'login' | 'logout') {
      try {
        localStorage.setItem(AUTH_EVENT_KEY, `${type}:${Date.now()}`);
      } catch {
        // 隐私模式 / localStorage 禁用时静默降级（仅本 tab 生效，不广播）
      }
    },
    clear() {
      this.token = '';
      this.user = null;
      this.meStatus = 'idle';
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      // 业务数据仍在 localStorage（EXP-005 数据链路约束，不破坏恢复链路）：
      // - wfb:history:v1（useUserData.ts HISTORY_KEY）
      // - wf_active_plan / wf_resume_plan_dismissed（misc-ui.js:87-88，M25 resume-plan-banner）
      // - wfb:analyze_task（HomeView TASK_KEY，老前端 task-polling.js）
      localStorage.removeItem('wfb:history:v1');
      localStorage.removeItem('wf_active_plan');
      localStorage.removeItem('wf_resume_plan_dismissed');
      localStorage.removeItem('wfb:analyze_task');
    },
    /**
     * 邀请码登录。
     *
     * 调用链：login → authApi.loginByInvite → 存 token / user → fetchMe 拉完整信息。
     *
     * @param inviteCode 邀请码字符串（来自 ElInput）
     * @throws 后端返回 4xx 时透传 axios error（调用方 try/catch）
     */
    async login(inviteCode: string) {
      const { data } = await authApi.loginByInvite(inviteCode.trim());
      this.setToken(data.token);
      this.setUser(data.user);
      // 登录成功后再拉一次完整信息（对齐老前端 submitLoginModalInvite 行为）
      try {
        await this.fetchMe();
      } catch (err) {
        // fetchMe 失败不阻塞登录流程（已拿到 token + 基础 user）
        console.warn('[auth] fetchMe after login failed:', err);
      }
      // 广播登录事件（其他 tab 若已登录则不动作；未登录则保持原状）
      this.broadcastAuthEvent('login');
    },
    /**
     * 拉 /api/auth/me 刷新 user 字段。
     *
     * 使用场景：
     * 1. 登录后调用，补全 invite 接口未返回的字段
     * 2. 应用启动时（main.ts）调用，确保 store 中的 user 与后端一致
     *
     * 失败处理：401 由 http.ts 拦截器统一处理（clear + 跳登录）；
     * 其他错误（如网络异常）仅记录日志，保持现有 state 不变。
     */
    async fetchMe() {
      if (!this.token) {
        this.meStatus = 'done';
        return;
      }
      this.meStatus = 'loading';
      try {
        const { data } = await authApi.me();
        this.setUser(data);
      } finally {
        this.meStatus = 'done';
      }
    },
    /**
     * 注销当前登录。
     *
     * 调用链：logout → authApi.logout（忽略错误）→ clear()。
     * 后端 logout 接口无状态（wb-routes.md §3.3），调用失败不影响本地清理。
     */
    async logout() {
      try {
        if (this.token) {
          await authApi.logout();
        }
      } catch (err) {
        console.warn('[auth] logout API failed:', err);
      } finally {
        this.clear();
        // 广播登出事件：其他 tab 监听到 storage event 后跳登录页
        this.broadcastAuthEvent('logout');
      }
    },
  },
});
